/* import __COLOCATED_TEMPLATE__ from './general.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type Action from 'embercom/models/workflow-connector/action';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';

interface Args {
  workflowAction: Action;
  nextSection: (sectionId: string) => void;
}

interface Signature {
  Args: Args;
}

export default class General extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  ignoredAttributeTypesForPreview = ['message', 'conversation', 'office_hours', 'team_capacity'];

  // Preserve the original fin usage type (fin vs fin_voice) so toggling
  // OFF then ON doesn't lose the fin_voice setting.
  private restoreUsage: string;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.restoreUsage = this.args.workflowAction.usage === 'fin_voice' ? 'fin_voice' : 'fin';
  }

  get enableFinCheckboxLabel() {
    if (this.appService.app.canUseFinSalesAgent) {
      return this.intl.t('workflow-connector.builder.body.general.usage.label-with-sdr');
    }
    if (!this.appService.app.canUseFinProcedures) {
      return this.intl.t('workflow-connector.builder.body.general.usage.label-for-tasks');
    }
    return this.intl.t('workflow-connector.builder.body.general.usage.label');
  }

  get enableFinCheckboxHint() {
    if (!this.appService.app.canUseFinProcedures) {
      return this.intl.t('workflow-connector.builder.body.general.usage.hint-for-tasks');
    }

    return this.intl.t('workflow-connector.builder.body.general.usage.hint');
  }

  get supportedActionUsage() {
    let options = [
      {
        text: this.intl.t('workflow-connector.builder.body.general.usage.fin.option'),
        value: 'fin',
        description: this.intl.t('workflow-connector.builder.body.general.usage.fin.description'),
      },
      {
        text: this.intl.t('workflow-connector.builder.body.general.usage.workflow-inbox.option'),
        value: 'workflow_and_inbox',
        description: this.intl.t(
          'workflow-connector.builder.body.general.usage.workflow-inbox.description',
        ),
      },
    ];

    // Add Fin Voice option if the feature flag is enabled
    if (this.appService.app.canUseFeature('fin-voice-actions')) {
      options.splice(1, 0, {
        text: this.intl.t('workflow-connector.builder.body.general.usage.fin-voice.option'),
        value: 'fin_voice',
        description: this.intl.t(
          'workflow-connector.builder.body.general.usage.fin-voice.description',
        ),
      });
    }

    return options;
  }

  get descriptionTitle() {
    if (
      this.args.workflowAction.usage === 'fin' ||
      this.args.workflowAction.usage === 'fin_voice'
    ) {
      return this.intl.t(
        'workflow-connector.builder.body.general.fin-trigger-guidance.description.title',
      );
    } else {
      return this.intl.t('workflow-connector.builder.body.general.description.label');
    }
  }

  get descriptionPlaceholder() {
    if (
      this.args.workflowAction.usage === 'fin' ||
      this.args.workflowAction.usage === 'fin_voice'
    ) {
      return this.intl.t(
        'workflow-connector.builder.body.general.fin-trigger-guidance.description.placeholder',
      );
    } else {
      return this.intl.t('workflow-connector.builder.body.general.description.placeholder');
    }
  }

  get app_id() {
    return this.appService.app.id;
  }

  get userUnprotectedFromSpoofing() {
    return !this.args.workflowAction.userProtectedFromSpoofing;
  }

  get isMessengerInstalledAndProtected() {
    return this.args.workflowAction.isMessengerInstalledAndProtected;
  }

  @action
  nextSection() {
    // All actions now go to the request/execution section which handles both server-side and client-side
    this.args.nextSection('request_section');
  }

  @action
  onUpdatePredicates(predicates: any) {
    this.args.workflowAction.updatePredicates(predicates);
    this.args.workflowAction.ruleset
      .get('fetchAudiencePreviewTask')
      .perform(this.ignoredAttributeTypesForPreview);
  }

  @action
  onUpdateRolePredicates(rolePredicates: any) {
    this.args.workflowAction.updateRolePredicates(rolePredicates);
    this.args.workflowAction.ruleset
      .get('fetchAudiencePreviewTask')
      .perform(this.ignoredAttributeTypesForPreview);
  }

  @action
  onUpdateOtpAuthEnabled() {
    this.args.workflowAction.updateOtpAuthEnabled();
  }

  @action
  onEnforceAuthChange() {
    let newValue = !this.args.workflowAction.enforceAuth;
    this.args.workflowAction.enforceAuth = newValue;
    this.args.workflowAction.noAuthenticationChosen = !newValue;
  }

  @action
  setDefaultRulesetIfEmpty() {
    if (this.args.workflowAction.usage === 'workflow_and_inbox') {
      this.args.workflowAction.noAuthenticationChosen = null;
      return;
    }

    this.args.workflowAction.setDefaultRuleset();
  }

  @action
  toggleActionUsage() {
    if (
      this.args.workflowAction.usage === 'fin' ||
      this.args.workflowAction.usage === 'fin_voice'
    ) {
      this.args.workflowAction.usage = 'workflow_and_inbox';
    } else {
      this.args.workflowAction.usage = this.restoreUsage;
    }
  }

  get isCustomMode() {
    return this.args.workflowAction.isCustomMode;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::Builder::Body::Sections::General': typeof General;
    'workflow-connector/builder/body/sections/general': typeof General;
  }
}
